/* CONTEXT:START
Component: Mock Joe Data Service
Status: build
Dependencies: JoeConfiguration interfaces
Next: Task 1.2 - JoeConfiguration main component
CONTEXT:END */

// Joe Configuration interfaces (following our established patterns)
export interface JoeConfiguration {
  id: string;                    // Natural key: "joe-config-v{version}"
  systemPrompt: string;          // 50-3500 characters
  userPromptTemplate: string;    // 50-2500 characters
  version: number;               // Version number for optimistic locking
  isActive: boolean;             // Only one active config at a time
  createdAt: string;             // ISO timestamp
  createdBy: string;             // User ID who created this config
  description?: string;          // Optional description of changes
}

export interface JoeConfigurationHistory {
  id: string;                    // Natural key: "joe-history-{timestamp}-{action}"
  configurationId: string;       // Reference to configuration
  action: 'CREATED' | 'ACTIVATED' | 'REVERTED';
  performedBy: string;           // User ID
  performedAt: string;           // ISO timestamp
  previousConfigId?: string;     // For reversion tracking
  notes?: string;                // Optional notes
}

export interface JoeTestRequest {
  systemPrompt: string;
  userPromptTemplate: string;
  testContent: string;
  testType: 'highlight' | 'lowlight';
}

export interface JoeTestResponse {
  feedback: string;
  processingTimeMs: number;
  tokenUsage?: number;
  success: boolean;
  error?: string;
}

// Default system prompt (current production prompt)
export const DEFAULT_SYSTEM_PROMPT = `You are an AI writing assistant that evaluates entries for a Monthly Business Review. These entries can be Highlights or Lowlights. Regardless of the entry type, you write targeting your audience: data driven, concise, matter of fact readers who want each report entry to have all of the information required for a STAAR format entry, but written as a paragraph. You will be providing an assessment, and gap analysis if they are gaps, on how good the entry is written for your audience.`;

// Default user prompt template (current production template)
export const DEFAULT_USER_PROMPT_TEMPLATE = `Assess the following Monthly Business Report entry for writing quality and provide suggestions on how to improve it. Identify and report gaps between the information given and how an ideal entry would look like. Here's the entry:

Entry Type: {{ENTRY_TYPE}}
Entry Content: {{ENTRY_CONTENT}}`;

// Mock active configuration
export const MOCK_ACTIVE_CONFIG: JoeConfiguration = {
  id: 'joe-config-v3',
  systemPrompt: DEFAULT_SYSTEM_PROMPT,
  userPromptTemplate: DEFAULT_USER_PROMPT_TEMPLATE,
  version: 3,
  isActive: true,
  createdAt: '2025-07-31T10:00:00Z',
  createdBy: 'admin@example.com',
  description: 'Updated for better STAAR format evaluation'
};

// Mock configuration history (5+ entries as required)
export const MOCK_CONFIG_HISTORY: JoeConfiguration[] = [
  {
    id: 'joe-config-v3',
    systemPrompt: DEFAULT_SYSTEM_PROMPT,
    userPromptTemplate: DEFAULT_USER_PROMPT_TEMPLATE,
    version: 3,
    isActive: true,
    createdAt: '2025-07-31T10:00:00Z',
    createdBy: 'admin@example.com',
    description: 'Updated for better STAAR format evaluation'
  },
  {
    id: 'joe-config-v2',
    systemPrompt: `You are an AI writing assistant that evaluates entries for a Monthly Business Review. Focus on clarity, impact, and professional tone when providing feedback.`,
    userPromptTemplate: `Please evaluate the following {{ENTRY_TYPE}} entry and provide specific feedback:

Content: {{ENTRY_CONTENT}}`,
    version: 2,
    isActive: false,
    createdAt: '2025-07-28T14:30:00Z',
    createdBy: 'admin@example.com',
    description: 'Simplified evaluation criteria'
  },
  {
    id: 'joe-config-v1',
    systemPrompt: `You are Joe, an AI assistant that helps evaluate monthly report submissions. Provide constructive feedback to help users improve their writing.`,
    userPromptTemplate: `Review this {{ENTRY_TYPE}} submission and suggest improvements:

{{ENTRY_CONTENT}}`,
    version: 1,
    isActive: false,
    createdAt: '2025-07-25T09:15:00Z',
    createdBy: 'system',
    description: 'Initial default configuration'
  },
  {
    id: 'joe-config-v4-draft',
    systemPrompt: `You are an AI writing assistant specializing in business communication evaluation. Your role is to assess Monthly Business Review entries for data-driven, concise, and professional content that follows STAAR format principles.`,
    userPromptTemplate: `Evaluate the following {{ENTRY_TYPE}} entry for business communication effectiveness:

Entry Type: {{ENTRY_TYPE}}
Content: {{ENTRY_CONTENT}}
Month: {{MONTH}}
Year: {{YEAR}}

Provide specific feedback on clarity, impact, and completeness.`,
    version: 4,
    isActive: false,
    createdAt: '2025-07-30T16:45:00Z',
    createdBy: 'admin@example.com',
    description: 'Draft version with enhanced STAAR focus (not activated)'
  },
  {
    id: 'joe-config-v0',
    systemPrompt: `You are a helpful AI assistant that reviews text submissions.`,
    userPromptTemplate: `Please review: {{ENTRY_CONTENT}}`,
    version: 0,
    isActive: false,
    createdAt: '2025-07-20T12:00:00Z',
    createdBy: 'system',
    description: 'Legacy configuration (deprecated)'
  }
];

// Mock configuration history entries
export const MOCK_HISTORY_ENTRIES: JoeConfigurationHistory[] = [
  {
    id: 'joe-history-1722427200000-ACTIVATED',
    configurationId: 'joe-config-v3',
    action: 'ACTIVATED',
    performedBy: 'admin@example.com',
    performedAt: '2025-07-31T10:00:00Z',
    previousConfigId: 'joe-config-v2',
    notes: 'Activated improved STAAR format configuration'
  },
  {
    id: 'joe-history-1722168600000-CREATED',
    configurationId: 'joe-config-v3',
    action: 'CREATED',
    performedBy: 'admin@example.com',
    performedAt: '2025-07-31T09:30:00Z',
    notes: 'Created new configuration with enhanced evaluation criteria'
  },
  {
    id: 'joe-history-1722168000000-REVERTED',
    configurationId: 'joe-config-v2',
    action: 'REVERTED',
    performedBy: 'admin@example.com',
    performedAt: '2025-07-28T14:30:00Z',
    previousConfigId: 'joe-config-v4-draft',
    notes: 'Reverted from draft v4 due to performance issues'
  }
];

// Mock test responses (success cases)
export const MOCK_TEST_RESPONSE_SUCCESS: JoeTestResponse = {
  feedback: `**Assessment**: This highlight entry demonstrates good structure and clear communication of achievements.

**Strengths**:
• Clear quantifiable results (25% improvement)
• Specific timeframe provided
• Direct business impact stated

**Areas for Improvement**:
• Consider adding the STAAR format elements:
  - **Situation**: What was the initial challenge or context?
  - **Task**: What specific task or goal was set?
  - **Action**: What specific actions were taken to achieve this result?
  - **Result**: The 25% improvement is good, but could be more specific

**Suggestions**:
1. Add context about what drove the need for improvement
2. Specify what actions or strategies led to the 25% gain
3. Include any lessons learned or next steps
4. Consider quantifying the business impact in dollars or other metrics

**Gap Analysis**: The entry provides good results but lacks the comprehensive storytelling that makes highlights truly impactful for executive audiences. Adding the missing STAAR elements would transform this from a good entry to an excellent one.

**Ready to Submit**: With the suggested improvements, this would be ready for inclusion in the monthly report.`,
  processingTimeMs: 8500,
  tokenUsage: 1247,
  success: true
};

// Mock test responses (error cases)
export const MOCK_TEST_RESPONSE_ERROR: JoeTestResponse = {
  feedback: '',
  processingTimeMs: 0,
  success: false,
  error: 'Test content is too short. Please provide at least 50 characters for meaningful evaluation.'
};

export const MOCK_TEST_RESPONSE_TIMEOUT: JoeTestResponse = {
  feedback: '',
  processingTimeMs: 15000,
  success: false,
  error: 'Request timed out. Joe is temporarily unavailable. Please try again later.'
};

// Mock test responses for different scenarios
export const MOCK_TEST_RESPONSES = {
  success: MOCK_TEST_RESPONSE_SUCCESS,
  error: MOCK_TEST_RESPONSE_ERROR,
  timeout: MOCK_TEST_RESPONSE_TIMEOUT,
  lowlight: {
    feedback: `**Assessment**: This lowlight entry effectively communicates a challenge and shows accountability.

**Strengths**:
• Honest acknowledgment of the issue
• Clear timeline provided
• Shows ownership and responsibility

**Areas for Improvement**:
• Apply STAAR format for better structure:
  - **Situation**: What led to this challenge?
  - **Task**: What was supposed to happen?
  - **Action**: What actions were taken to address the issue?
  - **Result**: What was learned and what are the next steps?

**Suggestions**:
1. Provide more context about root causes
2. Detail specific remediation actions taken
3. Include lessons learned and preventive measures
4. Quantify the impact where possible

**Gap Analysis**: While the entry shows good accountability, it would benefit from more analytical depth and forward-looking perspective that executives expect in lowlight discussions.

**Ready to Submit**: With additional detail on actions and lessons learned, this would be suitable for the monthly report.`,
    processingTimeMs: 7200,
    tokenUsage: 1156,
    success: true
  }
};

// Placeholder information for UI
export const PLACEHOLDER_INFO = {
  required: [
    '{{ENTRY_TYPE}} - highlight or lowlight',
    '{{ENTRY_CONTENT}} - the actual submission content'
  ],
  optional: [
    '{{MONTH}} - submission month',
    '{{YEAR}} - submission year'
  ],
  description: 'These placeholders will be automatically replaced with actual values when Joe evaluates submissions.'
};

// Mock service functions (Wave 1 - return mock data only)
export class MockJoeConfigurationService {
  
  async getActiveConfiguration(): Promise<JoeConfiguration> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));
    return MOCK_ACTIVE_CONFIG;
  }

  async getConfigurationHistory(limit: number = 10): Promise<JoeConfiguration[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    return MOCK_CONFIG_HISTORY.slice(0, limit);
  }

  async saveConfiguration(
    config: Omit<JoeConfiguration, 'id' | 'createdAt' | 'version'>
  ): Promise<JoeConfiguration> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const newConfig: JoeConfiguration = {
      ...config,
      id: `joe-config-v${Date.now()}`,
      createdAt: new Date().toISOString(),
      version: MOCK_ACTIVE_CONFIG.version + 1
    };
    
    return newConfig;
  }

  async testConfiguration(request: JoeTestRequest): Promise<JoeTestResponse> {
    // Simulate realistic processing time
    await new Promise(resolve => setTimeout(resolve, 2500));
    
    // Return different responses based on test content
    if (!request.testContent || request.testContent.trim().length < 50) {
      return MOCK_TEST_RESPONSE_ERROR;
    }
    
    if (request.testContent.toLowerCase().includes('timeout')) {
      return MOCK_TEST_RESPONSE_TIMEOUT;
    }
    
    if (request.testType === 'lowlight') {
      return MOCK_TEST_RESPONSES.lowlight;
    }
    
    return MOCK_TEST_RESPONSE_SUCCESS;
  }

  async revertConfiguration(configId: string, userId: string): Promise<void> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800));
    console.log(`MOCK: Reverted to configuration ${configId} by ${userId}`);
  }
}

// Export singleton instance for use in components
export const mockJoeConfigService = new MockJoeConfigurationService();
